import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Box, Paper, Typography, LinearProgress, Avatar } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../store";
import { setCurrentPage } from "../../../store/auth/authReducer";
import { useSnackbar } from "../../../utils/SnackbarContext";
import { logOut } from "../../../utils/logOut";
import TranslatedText from "../../../utils/Translate";
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import AudioFragment from "../../../utils/AudioFragment";

export default function ConversationCard() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const uniqueid = params.get('uniqueid') || '';
    const [detailRows, setDetailRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const dispatch = useAppDispatch();
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        dispatch(setCurrentPage('Conversation Card'));
    }, [dispatch]);

    async function fetchConversation() {
        if (!uniqueid) {
            setDetailRows([]);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.get(
                'https://cardgarantsales.com/restapi/transcript',
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + localStorage.getItem('access_token')
                    },
                    params: {
                        uniqueid,
                        transcriptonly: 1
                    }
                }
            );

            if (response.data !== null && typeof response.data === 'object') {
                if (Array.isArray(response.data) && response.data.length > 1) {
                    setDetailRows(response.data.slice(1));
                } else if (Array.isArray(response.data) && response.data.length === 1) {
                    setDetailRows(response.data);
                } else {
                    setDetailRows([response.data]);
                }
            } else {
                setDetailRows([]);
                setError('Invalid server response');
            }
        } catch (err: any) {
            console.log(err);
            if (err.code === 'ERR_BAD_REQUEST' || err.message?.includes('401')) {
                logOut(dispatch);
            } else if (err.code === 'ERR_NETWORK') {
                showSnackbar('ERROR NETWORK', 'error');
            } else {
                setError(err?.message || 'Failed to load conversation');
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (uniqueid) {
            fetchConversation();
        }
    }, [uniqueid]);

    type TranscriptFragment = {
        id: string;
        raw: string;
        text: string;
        speaker: 'robot' | 'client' | 'other';
    };

    const transcriptFragments = useMemo<TranscriptFragment[]>(() => {
        const rows = detailRows || [];

        const parseLine = (text: string, index: number): TranscriptFragment => {
            const trimmed = text.trim();
            const isRobot = trimmed.startsWith('=>');
            const isClient = trimmed.startsWith('<=');
            let content = trimmed;
            if (isRobot || isClient) {
                content = trimmed.slice(2).trim();
            }
            return {
                id: `${index}-${trimmed}`,
                raw: trimmed,
                text: content,
                speaker: isRobot ? 'robot' : isClient ? 'client' : 'other'
            };
        };

        if (rows.length === 1 && typeof rows[0].transcript === 'string' && rows[0].transcript.includes('\n')) {
            return rows[0].transcript.split(/\r?\n/).filter(Boolean).map((line: string, index: number) => parseLine(line, index));
        }

        return rows.map((row: any, index: number) => parseLine(String(row.transcript || ''), index));
    }, [detailRows]);

    return (
        <Box sx={{ width: '100%', px: 2, py: 2, maxWidth: 760, mx: 'auto' }}>
            {/* <Typography variant='h5' sx={{ mb: 1, textAlign: 'center', fontWeight: 700 }}>
                <TranslatedText text={'Conversation Card'} />
            </Typography> */}
            {/* {uniqueid ? (
                <Typography variant='body2' sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <TranslatedText text={'Unique ID:'} /> {uniqueid}
                </Typography>
            ) : (
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3, backgroundColor: '#f8f9ff' }}>
                    <Typography variant='body1' sx={{ color: 'text.primary', mb: 1 }}>
                        <TranslatedText text={'Open this page with'} />
                    </Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        /conversationdetails?uniqueid=[uniqueid]
                    </Typography>
                </Paper>
            )} */}
            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

            {error && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3, backgroundColor: '#f8d7da' }}>
                    <Typography color='error'>{error}</Typography>
                </Paper>
            )}

            {uniqueid && detailRows.length === 0 && !loading && !error && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                    <Typography>
                        <TranslatedText text={'No transcript details found for this uniqueid.'} />
                    </Typography>
                </Paper>
            )}

            {detailRows.length > 0 && (
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                    <Typography variant='subtitle1' gutterBottom>
                        <TranslatedText text={'Conversation'} />: {uniqueid}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            maxHeight: 600,
                            overflowY: 'auto',
                            p: 1,
                            borderRadius: 2,
                            backgroundColor: '#f9f9f9'
                        }}
                    >
                        {transcriptFragments.filter((fragment) => fragment.text.trim() !== '').reverse().map((fragment) => {
                            const isRobot = fragment.speaker === 'robot';
                            const isClient = fragment.speaker === 'client';
                            return (
                                <Box
                                    key={fragment.id}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: isClient ? 'flex-end' : 'flex-start'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            maxWidth: '80%'
                                        }}
                                    >
                                        {!isClient && (
                                            <Avatar
                                                sx={{
                                                    bgcolor: '#388e3c',
                                                    width: 32,
                                                    height: 32
                                                }}
                                            >
                                                <SmartToyIcon fontSize="small" />
                                            </Avatar>
                                        )}
                                        <Box
                                            sx={{
                                                width: '100%',
                                                p: 2,
                                                borderRadius: 2,
                                                backgroundColor: isRobot ? '#e8f5e9' : '#e3f2fd',
                                                border: `1px solid ${isRobot ? '#c8e6c9' : '#bbdefb'}`,
                                                position: 'relative'
                                            }}
                                        >
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    fontWeight: 600,
                                                    display: 'block',
                                                    mb: 0.5,
                                                    color: isRobot ? '#1b5e20' : '#0d47a1'
                                                }}
                                            >
                                                {isRobot ? 'Robot' : 'Client'}
                                            </Typography>
                                            <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: 'text.primary' }}>
                                                {fragment.text}
                                            </Typography>
                                        </Box>
                                        {isClient && (
                                            <Avatar
                                                sx={{
                                                    bgcolor: '#1976d2',
                                                    width: 32,
                                                    height: 32
                                                }}
                                            >
                                                <PersonIcon fontSize="small" />
                                            </Avatar>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#f5f5f5', border: '1px dashed #c0c0c0', mt: 3 }}>
                        <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
                            <TranslatedText text={'Audio'} />
                        </Typography>
                        <Box sx={{ height: 52, borderRadius: 1, backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d0d0d0' }}>
                            <AudioFragment id={detailRows[0]['conversation']} type={'recordedconversation'} />
                        </Box>
                    </Paper>
                </Paper>
            )}
        </Box>
    );
}
